import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import { Logout, NotificationsOff, Notifications } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { actions } from '@/redux/modules/Header';
import { connect, useDispatch, useSelector } from "react-redux";
import { Grid, LinearProgress } from '@mui/material';
import { api } from '@/api/Api';
import pm from "@/core/helpers/ProcessManager";
import { Queue, QueueResponse } from '@/api/CamundaController';
import { StateReducer } from '@/redux/modules/reducer';
import { QueueEnum } from '@/constatns';
import { removePushSubscription, createPushSubscription } from '@/core/helpers/WebPushUtil';
import { NotificationBanner } from './NotificationBanner';
import { todayAuditsText, useTodayAudits } from '@/pages/Audit/useTodayAudits';
import { selectAllInitialDepartments } from '@/redux/modules/department/departmentSelectors';
import { RolesData } from '@/interface';
import { redirectToLogin } from '@/core/server/axios';
import { clearSession } from '@/core/helpers/session';
import { CapitalGroup } from '@/core/helpers/charterCapital';
import { CharterCapitalDialog } from './CharterCapital/CharterCapitalDialog';

const drawerWidth = 280;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    // Mobile first styles
    [theme.breakpoints.down('md')]: {
        width: '100%',
        marginLeft: 0,
    },
    [theme.breakpoints.up('md')]: {
        ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    },
}));

const basePath = process.env.PUBLIC_URL;

interface HeaderProps {
    userLogin: string;
    rolesData?: RolesData;
    activeKassaId: number;
    userRole: string;
    menuBarOpen: boolean;
    isLoading: boolean;
    kassaQueueList?: Queue;
    /** Уставной капитал по юрлицам, считается в AuthorizedLayout */
    charterCapitalGroups?: CapitalGroup[];
}

const Header = ({ userLogin, userRole, rolesData, activeKassaId, menuBarOpen, isLoading, kassaQueueList, charterCapitalGroups = [] }: HeaderProps) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // anchorEl при закрытии не сбрасываем: без него MUI на время анимации
    // закрытия позиционирует меню по body, и оно улетает в правый нижний угол
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [open, setOpen] = React.useState<boolean>(false);
    const [hasPushSubscription, setHasPushSubscription] = React.useState<boolean>(false);
    const [haveActiveAfm, setHaveActiveAfm] = React.useState<boolean>(false);
    const [haveOvertime, setHaveOvertime] = React.useState<boolean>(false);
    // Главный кассир, менеджер и директор: напоминание о сегодняшней ревизии по их отделениям
    const todayAudits = useTodayAudits();
    const departmentList = useSelector(selectAllInitialDepartments);
    const [charterCapitalOpen, setCharterCapitalOpen] = React.useState<boolean>(false);
    const haveCharterCapitalShortage = charterCapitalGroups.some(group => group.shortage > 0);

    React.useEffect(() => {
        const checkSubscription = async () => {

            if (!('serviceWorker' in navigator)) {
                return;
            }

            if (!('PushManager' in window)) {
                return;
            }

            try {
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('ServiceWorker timeout')), 3000)
                );

                const registrationPromise = navigator.serviceWorker.ready;

                const registration = await Promise.race([registrationPromise, timeoutPromise]) as ServiceWorkerRegistration;
                const subscription = await registration.pushManager.getSubscription();

                setHasPushSubscription(!!subscription);
            } catch (error) {
                console.warn('[Header] Error checking push subscription:', error);
                setHasPushSubscription(false);
            }
        };
        checkSubscription();
    }, []);

    React.useEffect(() => {
        if (!activeKassaId) return;

        checkHaveAFM(+activeKassaId);
        checkHaveOvertime(+activeKassaId);

        const interval = setInterval(() => {
            checkHaveAFM(+activeKassaId);
            checkHaveOvertime(+activeKassaId);
        }, 1_200_000); // 20 мин

        return () => clearInterval(interval);
    }, [activeKassaId]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleClickProfile = () => {
        handleClose();
        dispatch(actions.selectMenu({ path: '/profile', title: 'Мой профиль', index: 10 }));
        dispatch(actions.openQueueMenuBar(false));
        // Страница профиля грузится лениво: без transition Suspense прячет текущий
        // экран на время загрузки чанка и обрывает анимацию закрытия меню,
        // из-за чего оно оставалось висеть на экране
        React.startTransition(() => navigate('/profile'));
    };

    const handleUnsubscribePush = async () => {
        try {
            pm.openBackDrop('Отписка от уведомлений...');

            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                pm.closeBackDrop();
                pm.openSnackBar('Уведомления не поддерживаются браузером', { severity: 'error' });
                handleClose();
                return;
            }
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (!subscription) {
                pm.closeBackDrop();
                pm.openSnackBar('Активная подписка не найдена', { severity: 'warning' });
                setHasPushSubscription(false);
                handleClose();
                return;
            }

            const endpoint = subscription.endpoint;

            if (!userLogin) {
                pm.closeBackDrop();
                pm.openSnackBar('Ошибка: не найден логин пользователя', { severity: 'error' });
                handleClose();
                return;
            }
            await api.webPush.unsubscribe(endpoint, userLogin);
            await removePushSubscription();

            setHasPushSubscription(false);
            pm.closeBackDrop();
            pm.openSnackBar('Вы успешно отписались от уведомлений', { severity: 'success' });
        } catch (error) {
            console.error('Error unsubscribing from push:', error);
            pm.closeBackDrop();
            pm.openSnackBar('Ошибка при отписке от уведомлений', { severity: 'error' });
        }
        handleClose();
    };

    const handleSubscribePush = async () => {
        try {
            pm.openBackDrop('Подписка на уведомления...');

            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                pm.closeBackDrop();
                pm.openSnackBar('Push-уведомления не поддерживаются браузером', { severity: 'error' });
                handleClose();
                return;
            }

            if (!userLogin) {
                pm.closeBackDrop();
                pm.openSnackBar('Ошибка: не найден логин пользователя', { severity: 'error' });
                handleClose();
                return;
            }

            const subscription = await createPushSubscription();

            if (!subscription) {
                pm.closeBackDrop();
                pm.openSnackBar('Не удалось создать подписку', { severity: 'error' });
                handleClose();
                return;
            }

            await api.webPush.saveSubscription(subscription.toJSON(), userLogin, userRole);

            setHasPushSubscription(true);
            pm.closeBackDrop();
            pm.openSnackBar('Вы успешно подписались на уведомления!', { severity: 'success' });
        } catch (error) {
            console.error('Error subscribing to push:', error);
            pm.closeBackDrop();

            if (error instanceof Error) {
                if (error.message === 'Permission denied') {
                    pm.openSnackBar('Вы отклонили разрешение на уведомления. Разрешите в настройках браузера.', { severity: 'warning' });
                } else {
                    pm.openSnackBar('Ошибка при подписке на уведомления: ' + error.message, { severity: 'error' });
                }
            } else {
                pm.openSnackBar('Ошибка при подписке на уведомления', { severity: 'error' });
            }
        }
        handleClose();
    };

    const handleLogout = async () => {
        if (rolesData?.isCashier || !!rolesData?.mainCashierCity) {
            await api.kassa.checkKassaIsOpen({ kassaId: `${activeKassaId}` })
                .then(async res => {
                    if (res?.status === 1 && (res?.model?.login === userLogin || res?.model?.closeLogin === userLogin)) {
                        kassaQueueList?.id && await api.camunda.getQueueList(kassaQueueList?.id, 1, 100)
                            .then(async (res: QueueResponse) => {
                                const currentProcess = res?.queue?.find((queueRes) => queueRes.login === userLogin);
                                if (currentProcess) {
                                    await api.camunda.completeTask({
                                        processInstanceId: currentProcess?.processInstanceId,
                                        taskId: currentProcess?.taskId,
                                        variables: {
                                            button: {
                                                forth: false,
                                                exit: false,
                                                retry: true,
                                                back: false,
                                                save: false
                                            },
                                            login: "SHIFT"
                                        }
                                    });
                                }
                            })
                            .finally(() => pm.closeBackDrop());
                    } else {
                        userLogin && await api.qrAuth.logout(userLogin)
                            .then((res) => {
                                console.log(res);
                            });
                    }
                })
                .catch(() => pm.openSnackBar("Произошла ошибка: Не удалось снять смену", { severity: "error" }));
        } else {
            userLogin && await api.qrAuth.logout(userLogin)
                .then((res) => {
                    console.log(res);
                });
        }

        clearSession();

        redirectToLogin('пользователь нажал "Выйти"');
        dispatch(actions.logout());
    };

    const checkHaveOvertime = async (activeKassaId: number) => {
        api.request.checkOvertime(activeKassaId)
            .then((res: number) => {
                setHaveOvertime(typeof res === 'number' && res > 0);
            })
            .catch(() => {
                setHaveOvertime(false);
            });
    };

    const checkHaveAFM = async (activeKassaId: number) => {
        api.request.getAfmApp(activeKassaId)
            .then((res: { hasAfm: number }) => {
                setHaveActiveAfm(res.hasAfm > 0);
            })
            .catch(() => {
                setHaveActiveAfm(false);
            });
    };

    return (
        <AppBar position="fixed" open={menuBarOpen}>
            <div style={isLoading ? { display: "block" } : { visibility: "hidden" }}>
                <LinearProgress />
            </div>
            <Toolbar
                sx={{
                    height: 50,
                    minHeight: 50,
                    position: "relative",
                    overflow: "hidden",
                    background: "#EEEEEE",
                    color: "rgba(0, 0, 0, 0.54)",
                    pr: '24px',
                    pl: '16px',
                }}
            >
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="open drawer"
                    onClick={() => dispatch(actions.openMenuBar(!menuBarOpen))}
                >
                    <MenuIcon />
                </IconButton>
                <IconButton href='/' sx={{ height: "70%" }}>
                    <img src={`${basePath}/logo.png`} style={{
                        display: "inline-block",
                        height: "100%"
                    }} />
                </IconButton>

                <Grid container justifyContent={'center'} alignItems="center" sx={{ ml: 2, mr: 2 }}>
                    <NotificationBanner
                        haveOvertime={haveOvertime}
                        haveActiveAfm={haveActiveAfm}
                        todayAuditText={todayAuditsText(todayAudits, departmentList)}
                        haveCharterCapitalShortage={haveCharterCapitalShortage}
                        onCharterCapitalClick={() => setCharterCapitalOpen(true)}
                    />
                </Grid>
                <CharterCapitalDialog
                    open={charterCapitalOpen}
                    onClose={() => setCharterCapitalOpen(false)}
                    groups={charterCapitalGroups}
                />

                <div style={{ flexGrow: 1 }} />

                <IconButton onClick={handleClick}
                    size={"small"}
                    sx={{ ml: 2 }}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}>
                    <Avatar
                        alt="User Avatar"
                        src={"/smtava.jpeg"}
                        sx={{
                            width: 40,
                            height: 40
                        }}
                    />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={open}
                    onClose={handleClose}
                    onClick={handleClose}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem onClick={handleClickProfile} >
                        <Avatar /> Профиль
                    </MenuItem>
                    <Divider />
                    {(
                        rolesData?.isDirector ||
                        rolesData?.isAdmin ||
                        rolesData?.isTreasurer ||
                        !!rolesData?.mainCashierCity
                    ) && (hasPushSubscription ? (
                        <MenuItem onClick={handleUnsubscribePush}>
                            <ListItemIcon>
                                <NotificationsOff fontSize="small" />
                            </ListItemIcon>
                            Отключить уведомления
                        </MenuItem>
                    ) : (
                        <MenuItem onClick={handleSubscribePush}>
                            <ListItemIcon>
                                <Notifications fontSize="small" />
                            </ListItemIcon>
                            Подписаться на уведомления
                        </MenuItem>
                    ))}
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <Logout fontSize="small" />
                        </ListItemIcon>
                        Logout
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

function mapStateToProps(state: StateReducer) {

    return {
        userLogin: state?.header?.userContext?.user?.login || "",
        rolesData: state?.header?.rolesData,
        activeKassaId: state?.departmentReducer?.activeKassaId,
        userRole: (state?.header?.userContext?.user?.roles || [])[0]?.code || "",
        menuBarOpen: state?.header?.menuBarOpen || false,
        isLoading: state?.process?.isLoading,
        kassaQueueList: state?.header?.userQueue?.find((queue) => queue.process === QueueEnum.KassaDay)
    };
}

export default connect(mapStateToProps, null)(Header);